// Karta przypisanego pokoju z możliwością inline editing

import React, { useState } from "react";
import { useAccommodation } from "../../AccommodationProvider";
import { AssignedRoom } from "../../types/accommodation.types";

interface AssignedRoomCardProps {
  room: AssignedRoom;
}

const AssignedRoomCard: React.FC<AssignedRoomCardProps> = ({ room }) => {
  const { removeRoom, updateRoomPrice, updateRoomQuantity } =
    useAccommodation();

  // Stan edycji
  const [isEditingPrice, setIsEditingPrice] = useState(false);
  const [isEditingQuantity, setIsEditingQuantity] = useState(false);
  const [editPrice, setEditPrice] = useState(
    typeof room.unitPrice === "number"
      ? room.unitPrice
      : parseFloat(room.unitPrice || "0")
  );
  const [editQuantity, setEditQuantity] = useState(room.quantity);
  const [isLoading, setIsLoading] = useState(false);

  // Oblicz liczbę osób w pokoju
  const personsInRoom = React.useMemo(() => {
    switch (room.occupancyType) {
      case "pairs":
        return 2;
      case "singles_shared":
        return (
          room.personsCountPerRoom || room.categoryOption.personsPerRoom || 1
        );
      case "singles_alone":
        return 1;
      default:
        return 1;
    }
  }, [
    room.occupancyType,
    room.personsCountPerRoom,
    room.categoryOption.personsPerRoom,
  ]);

  const totalPersons = room.quantity * personsInRoom;
  const unitPrice =
    typeof room.unitPrice === "number"
      ? room.unitPrice
      : parseFloat(room.unitPrice || "0");

  // Handle usuwania pokoju
  const handleRemoveRoom = async () => {
    if (window.confirm("Czy na pewno chcesz usunąć ten pokój?")) {
      setIsLoading(true);
      try {
        await removeRoom(room.id);
      } catch (error) {
        console.error("Failed to remove room:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Handle zapisywania ceny
  const handleSavePrice = async () => {
    if (editPrice !== room.unitPrice) {
      setIsLoading(true);
      try {
        await updateRoomPrice(room.id, editPrice);
      } catch (error) {
        console.error("Failed to update price:", error);
        setEditPrice(
          typeof room.unitPrice === "number"
            ? room.unitPrice
            : parseFloat(room.unitPrice || "0")
        ); // Rollback
      } finally {
        setIsLoading(false);
      }
    }
    setIsEditingPrice(false);
  };

  // Handle anulowania edycji ceny
  const handleCancelEditPrice = () => {
    setEditPrice(
      typeof room.unitPrice === "number"
        ? room.unitPrice
        : parseFloat(room.unitPrice || "0")
    );
    setIsEditingPrice(false);
  };

  // Handle zapisywania ilości
  const handleSaveQuantity = async () => {
    if (editQuantity !== room.quantity && editQuantity > 0) {
      setIsLoading(true);
      try {
        await updateRoomQuantity(room.id, editQuantity);
      } catch (error) {
        console.error("Failed to update quantity:", error);
        setEditQuantity(room.quantity); // Rollback
      } finally {
        setIsLoading(false);
      }
    }
    setIsEditingQuantity(false);
  };

  // Handle anulowania edycji ilości
  const handleCancelEditQuantity = () => {
    setEditQuantity(room.quantity);
    setIsEditingQuantity(false);
  };

  return (
    <div className="border-b border-gray-200 py-2 px-3 last:border-b-0">
      <div className="flex items-center justify-between">
        {/* Lewa strona: Kompaktowe info */}
        <div className="flex-1 text-left">
          <div className="text-sm font-medium text-gray-900 mb-1">
            {room.categoryOption.name}
          </div>
          <div className="text-xs text-gray-600">
            {isEditingQuantity ? (
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  min="1"
                  value={editQuantity}
                  onChange={(e) =>
                    setEditQuantity(parseInt(e.target.value) || 1)
                  }
                  onBlur={handleSaveQuantity}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSaveQuantity();
                    } else if (e.key === "Escape") {
                      e.preventDefault();
                      handleCancelEditQuantity();
                    }
                  }}
                  className="w-12 px-1 py-0.5 text-xs border border-blue-300 rounded focus:outline-none focus:border-blue-500"
                  autoFocus
                />
                <span>
                  pok. × {personsInRoom} os. = {editQuantity * personsInRoom}{" "}
                  os.
                </span>
              </div>
            ) : (
              <button
                onClick={() => setIsEditingQuantity(true)}
                disabled={isEditingPrice || isLoading}
                className="hover:bg-gray-100 px-1 py-0.5 rounded text-left"
                title="Kliknij aby edytować ilość pokoi"
              >
                {room.quantity} pok. × {personsInRoom} os. = {totalPersons} os.
              </button>
            )}
          </div>
        </div>

        {/* Prawa strona: Cena i akcje */}
        <div className="flex items-center gap-3">
          {isEditingPrice ? (
            <div className="flex items-center gap-1">
              <input
                type="number"
                min="0"
                step="0.01"
                value={editPrice}
                onChange={(e) => setEditPrice(parseFloat(e.target.value) || 0)}
                onBlur={handleSavePrice}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSavePrice();
                  } else if (e.key === "Escape") {
                    e.preventDefault();
                    handleCancelEditPrice();
                  }
                }}
                className="w-20 px-1 py-0.5 text-xs border border-blue-300 rounded focus:outline-none focus:border-blue-500"
                autoFocus
              />
              <span className="text-xs text-gray-500">zł</span>
            </div>
          ) : (
            <button
              onClick={() => setIsEditingPrice(true)}
              disabled={isEditingQuantity || isLoading}
              className="text-sm text-gray-800 hover:bg-gray-100 px-2 py-1 rounded text-right"
              title="Kliknij aby edytować cenę"
            >
              {unitPrice.toFixed(2)} zł/pok.
            </button>
          )}

          {/* Przycisk usuwania */}
          <button
            onClick={handleRemoveRoom}
            disabled={isLoading}
            className="text-red-600 hover:text-red-800 disabled:opacity-50 text-sm"
            title="Usuń pokoje"
          >
            {isLoading ? "⏳" : "🗑️"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignedRoomCard;
